import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { trashApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Trash2, RotateCcw, AlertTriangle, FileText, Calendar, Loader2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';

const TrashPage = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTrash = async () => {
    setLoading(true);
    try { const r = await trashApi.getAll(); setItems(r.data); }
    catch { toast.error('Error al cargar papelera'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTrash(); }, []);

  const handleRestore = async (type, id) => {
    try {
      await trashApi.restore(type, id);
      toast.success('Restaurado exitosamente');
      fetchTrash();
    } catch { toast.error('Error al restaurar'); }
  };

  const handlePermanentDelete = async (type, id) => {
    try {
      await trashApi.permanentDelete(type, id);
      toast.success('Eliminado permanentemente');
      fetchTrash();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleEmptyTrash = async () => {
    if (items.length === 0) return;
    try {
      await trashApi.empty();
      toast.success('Papelera vaciada');
      setItems([]);
    } catch { toast.error('Error al vaciar papelera'); }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const typeLabels = { content: 'Contenido', document: 'Documento' };
  const typeIcons = { content: Calendar, document: FileText };

  return (
    <DashboardLayout title="Papelera">
      <div className="space-y-6" data-testid="trash-page">
        <div className="flex items-center justify-between">
          <div>
            <p className={cn('text-sm', isDark ? 'text-zinc-400' : 'text-zinc-500')}>
              {items.length} elemento(s) en la papelera
            </p>
          </div>
          {items.length > 0 && (
            <Button variant="outline" onClick={handleEmptyTrash} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="empty-trash-btn">
              <Trash2 size={16} className="mr-2" />Vaciar Papelera
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 size={32} className="animate-spin text-zinc-500" /></div>
        ) : items.length === 0 ? (
          <Card className={cn(isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
            <CardContent className="py-16 text-center">
              <Trash2 size={48} className="mx-auto mb-4 text-zinc-600" />
              <h3 className={cn('text-lg font-medium mb-1', isDark ? 'text-zinc-300' : 'text-zinc-700')}>Papelera vacia</h3>
              <p className={cn('text-sm', isDark ? 'text-zinc-500' : 'text-zinc-400')}>Los elementos eliminados apareceran aqui</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const Icon = typeIcons[item.type] || FileText;
              return (
                <Card key={`${item.type}-${item.id}`} className={cn(isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')} data-testid={`trash-item-${item.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', isDark ? 'bg-[#1A1A1A]' : 'bg-zinc-100')}>
                      <Icon size={20} className="text-zinc-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className={cn('font-medium truncate', isDark ? 'text-white' : 'text-zinc-900')}>{item.title}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={item.type === 'content' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}>
                          {typeLabels[item.type]}
                        </Badge>
                        <span className={cn('text-xs', isDark ? 'text-zinc-500' : 'text-zinc-400')}>
                          Eliminado {formatDate(item.deleted_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleRestore(item.type, item.id)} className="text-green-400 hover:text-green-300 hover:bg-green-500/10" data-testid={`restore-${item.id}`}>
                        <RotateCcw size={16} className="mr-1" />Restaurar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handlePermanentDelete(item.type, item.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`permanent-delete-${item.id}`}>
                        <Trash2 size={16} className="mr-1" />Eliminar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {items.length > 0 && (
          <div className={cn('p-4 rounded-xl flex items-start gap-3', isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200')}>
            <AlertTriangle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <p className={cn('text-sm', isDark ? 'text-amber-200/70' : 'text-amber-700')}>
              Los elementos en la papelera se pueden restaurar. "Vaciar Papelera" los eliminara permanentemente.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TrashPage;
